import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Card, Input, Space } from 'antd';
import { FileTextOutlined, AlignLeftOutlined, VerticalAlignTopOutlined, VerticalAlignBottomOutlined, ScissorOutlined, FontSizeOutlined } from '@ant-design/icons';
import NodeLabel from './NodeLabel';
import { useParams } from 'react-router-dom';
import api from '../../../api/client';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';

const TextSlicingNode = ({ id, data, selected }) => {
    const { setNodes } = useReactFlow();
    const allEdges = useEdges();
    const allNodes = useNodes();
    const [hovered, setHovered] = useState(false);

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    // Prefer canvasId from data (passed from Editor) but fallback to params just in case
    const { projectId } = useParams();
    const canvasId = data.canvasId || projectId;

    // --- State ---
    const [startPattern, setStartPattern] = useState(data.startPattern || '');
    const [endPattern, setEndPattern] = useState(data.endPattern || '');
    const [slices, setSlices] = useState(data.slices || []);
    const [incomingText, setIncomingText] = useState('');

    // --- Refs for stable callbacks ---
    const onSaveRef = useRef(data.onSave);
    useEffect(() => { onSaveRef.current = data.onSave; }, [data.onSave]);

    // --- Data Persistence ---
    const updateData = useCallback((updates) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) return { ...node, data: { ...node.data, ...updates } };
            return node;
        }));
        if (onSaveRef.current) onSaveRef.current(id, updates);
    }, [id, setNodes]);

    // --- Sync State from Data ---
    useEffect(() => {
        if (data.startPattern !== undefined && data.startPattern !== startPattern) setStartPattern(data.startPattern);
        if (data.endPattern !== undefined && data.endPattern !== endPattern) setEndPattern(data.endPattern);
    }, [data.startPattern, data.endPattern, startPattern, endPattern]);

    // --- Handle Input Text ---
    useEffect(() => {
        // Find input edge connected to this node
        const inputEdge = allEdges.find((edge) => edge.target === id);

        if (inputEdge) {
            // We need to look up the source node, but depending on `allNodes` triggers this effect
            // every time ANY node changes (position, selection, etc).
            // This is a major cause of the "Maximum update depth" error if `setNodes` is called downstream.

            const sourceNode = allNodes.find((node) => node.id === inputEdge.source);

            if (sourceNode) {
                const text = sourceNode.data?.text || sourceNode.data?.output || '';
                if (text !== incomingText) {
                    setIncomingText(text);
                }
            }
        } else {
            if (incomingText !== '') {
                setIncomingText('');
            }
        }
    }, [allEdges, id, incomingText, allNodes]); // We still depend on allNodes but Editor.js logic should stabilize it.

    // --- Backend Sync Logic ---
    const syncTimeoutRef = useRef(null);

    const syncToBackend = useCallback(async (currentSlices) => {
        if (!canvasId) {
            console.warn('TextSlicingNode: No canvasId, cannot sync slices.');
            return;
        }

        console.log('TextSlicingNode: Syncing slices...', { canvasId, nodeId: id, count: currentSlices.length });

        try {
            // First, clear existing slices for this node
            await api.delete(`/canvases/${canvasId}/nodes/${id}/text-slices`);

            // Then create new slices
            if (currentSlices.length > 0) {
                const promises = currentSlices.map((sliceContent, index) => {
                    return api.post(`/canvases/${canvasId}/text-slices`, {
                        node_id: id,
                        slice_index: index,
                        content: sliceContent,
                        system: { id: 'system', email: 'system@pxlplygrnd.com' }
                    });
                });
                await Promise.all(promises);
            }
            console.log('TextSlicingNode: Successfully synced text slices to backend');
        } catch (error) {
            console.error('TextSlicingNode: Failed to sync text slices:', error);
        }
    }, [canvasId, id]);

    // --- Slicing Logic ---
    useEffect(() => {
        console.log('TextSlicingNode: Slicing logic triggered', { incomingText, startPattern, endPattern });

        if (!incomingText) {
            // Fix for race condition on load:
            // If we have an input connected, and that input HAS text, we should not clear slices yet.
            // We should wait for incomingText to be updated by the other useEffect.
            const inputEdge = allEdges.find((edge) => edge.target === id);
            if (inputEdge) {
                const sourceNode = allNodes.find((node) => node.id === inputEdge.source);
                const sourceHasText = sourceNode?.data?.text || sourceNode?.data?.output;
                if (sourceHasText) {
                    console.log('TextSlicingNode: Skipping clear - source has text, waiting for sync');
                    return;
                }
            }

            if (slices.length > 0) {
                console.log('TextSlicingNode: Clearing slices due to empty input');
                setSlices([]);
                updateData({ slices: [] });
                if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
                syncTimeoutRef.current = setTimeout(() => syncToBackend([]), 1000);
            }
            return;
        }

        let newSlices = [];

        // Logic 1: Start/Stop Extraction
        if (startPattern && endPattern) {
            console.log('TextSlicingNode: Using Start/End Pattern Extraction');
            try {
                let regex;
                // If patterns are identical, use lookahead to allow the end pattern of one slice 
                // to serve as the start pattern of the next slice.
                if (startPattern === endPattern) {
                    regex = new RegExp(`${startPattern}([\\s\\S]*?)(?=${endPattern})`, 'g');
                } else {
                    regex = new RegExp(`${startPattern}([\\s\\S]*?)${endPattern}`, 'g');
                }

                // Use matchAll to find all occurrences
                const matches = [...incomingText.matchAll(regex)];

                // Group 1 contains the text strictly between patterns
                if (matches.length > 0) {
                    newSlices = matches.map(m => m[1].trim()).filter(s => s !== '');
                    console.log('TextSlicingNode: Extracted slices:', newSlices);
                } else {
                    console.log('TextSlicingNode: No matches found for start/end patterns');
                    newSlices = [];
                }
            } catch (e) {
                console.error("TextSlicingNode: Invalid Regex for Start/End patterns", e);
                newSlices = [];
            }
        }
        // Logic 2: Fallback (Whole Text)
        else {
            console.log('TextSlicingNode: No patterns, using full text');
            newSlices = [incomingText];
        }

        // Compare and Update
        if (JSON.stringify(newSlices) !== JSON.stringify(slices)) {
            console.log('TextSlicingNode: Slices changed, updating state and triggering sync...');
            setSlices(newSlices);
            updateData({ slices: newSlices });

            if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
            syncTimeoutRef.current = setTimeout(() => syncToBackend(newSlices), 1000);
        } else {
            console.log('TextSlicingNode: Slices unchanged, skipping update');
        }

    }, [incomingText, startPattern, endPattern, slices, updateData, syncToBackend]);

    const handleStartPatternChange = (e) => {
        const val = e.target.value;
        setStartPattern(val);
        updateData({ startPattern: val });
    };

    const handleEndPatternChange = (e) => {
        const val = e.target.value;
        setEndPattern(val);
        updateData({ endPattern: val });
    };

    return (
        <div
            style={{ position: 'relative', height: 'auto', minHeight: '100%', width: '100%', minWidth: 250 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer isVisible={selected} minWidth={250} minHeight={200}
                onResizeEnd={(e, p) => data.onResize && data.onResize(id, { ...p })} />

            <NodeLabel title="Text Slicer" iconType="scissor" color="#eb2f96" />

            {/* Debug / Status Indicator */}
            <div style={{
                position: 'absolute', top: -20, right: 0,
                fontSize: 10, color: incomingText ? '#52c41a' : '#8b949e',
                fontWeight: 'bold', textTransform: 'uppercase'
            }}>
                {incomingText ? `Input: ${incomingText.length} chars` : 'No Input'}
            </div>

            {/* Input Handle - Large Style */}
            <Handle type="target" position={Position.Left} id="input-text" style={getHandleStyle(TYPE_COLORS.text, 'left', handlesVisible)} />

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: 'auto', minHeight: '100%', display: 'flex', flexDirection: 'column', background: '#161b22', border: '1px solid #30363d' }}
                style={{ width: '100%', height: 'auto', minHeight: '100%', borderRadius: 8, overflow: 'visible', border: 'none' }}
            >
                {/* Patterns Area */}
                <div style={{ padding: '12px', borderBottom: '1px solid #30363d', background: '#0d1117', display: 'flex', flexDirection: 'column', gap: 8 }}>

                    {/* Start/Stop Extraction */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                            <div style={{ color: '#8b949e', fontSize: 10, fontWeight: 'bold', marginBottom: 4, textTransform: 'uppercase' }}>
                                Start Pattern
                            </div>
                            <Input
                                prefix={<VerticalAlignTopOutlined style={{ color: '#eb2f96' }} />}
                                value={startPattern}
                                onChange={handleStartPatternChange}
                                placeholder="Start"
                                size="small"
                                style={{ background: '#161b22', border: '1px solid #30363d', color: '#c9d1d9' }}
                            />
                        </div>
                        <div>
                            <div style={{ color: '#8b949e', fontSize: 10, fontWeight: 'bold', marginBottom: 4, textTransform: 'uppercase' }}>
                                Stop Pattern
                            </div>
                            <Input
                                prefix={<VerticalAlignBottomOutlined style={{ color: '#eb2f96' }} />}
                                value={endPattern}
                                onChange={handleEndPatternChange}
                                placeholder="Stop"
                                size="small"
                                style={{ background: '#161b22', border: '1px solid #30363d', color: '#c9d1d9' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Slices List */}
                <div className="nodrag" style={{
                    flex: 1,
                    // overflowY: 'auto', // Removed for handle visibility
                    padding: '8px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}>
                    {slices.length === 0 ? (
                        <div style={{ padding: 12, textAlign: 'center', color: '#8b949e', fontSize: 12 }}>
                            Connect text input and set patterns to slice.
                        </div>
                    ) : (
                        slices.map((slice, idx) => (
                            <div key={idx} style={{
                                position: 'relative',
                                padding: '8px 12px',
                                background: '#21262d',
                                borderBottom: '1px solid #30363d',
                                fontSize: 12,
                                color: '#c9d1d9',
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: 60
                            }}>
                                <span style={{ marginRight: 40, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 14 }}>
                                    {slice}
                                </span>

                                {/* Output Handle for this slice - Mini "Main" Style */}
                                <Handle
                                    type="source"
                                    position={Position.Right}
                                    id={`slice-${idx}`}
                                    style={{
                                        ...getHandleStyle(TYPE_COLORS.text, 'right', handlesVisible),
                                        width: 30, height: 30,
                                        right: -35, top: '50%',
                                        transform: 'translateY(-50%)'
                                    }}
                                />
                            </div>
                        ))
                    )}
                </div>
            </Card>
        </div>
    );
};

export default memo(TextSlicingNode);
